import { Button } from "@/components/ui/button";
import { useGetClientSubscription } from "@/features/admin/api/client.queries";
import { formatSubscriptionDate } from "@/lib/dateUtils";
import type { ClientSubscriptionsResponse } from "@/features/admin/types/client.types";

interface SubscriptionProps {
  userId?: string;
  theme?: "light" | "dark";
}

const platformIcons: Record<string, string> = {
  facebook: "/logo/fb-logo.svg",
  twitter: "/logo/x-logo.svg",
  youtube: "/logo/yt-logo.svg",
  tiktok: "/logo/tiktok-logo.svg",
  reddit: "/logo/reddit-logo.svg",
  instagram: "/logo/ig-logo.svg",
};

export default function Subscription({
  userId,
  theme = "dark",
}: SubscriptionProps) {
  const { data, isLoading, isError } = useGetClientSubscription(userId || "");

  const subscriptionData = data as ClientSubscriptionsResponse | undefined;

  const getStatusColor = (status: string) => {
    return status.toLowerCase() === "active" ? "bg-[#00FF4C]" : "bg-gray-500";
  };

  const getThemeClasses = () => {
    if (theme === "light") {
      return {
        container: "bg-white",
        title: "text-neutral-950",
        subtitle: "text-neutral-500",
        label: "text-neutral-500",
        value: "text-neutral-950",
        button: "text-gray-700 hover:bg-gray-100 hover:text-gray-900",
        skeleton: "bg-gray-300",
        rowHover: "hover:bg-gray-50",
      };
    }
    return {
      container: "bg-black-950",
      title: "text-white",
      subtitle: "text-black-500",
      label: "text-black-500",
      value: "text-white",
      button: "text-white hover:bg-white/30 hover:text-white",
      skeleton: "bg-white/10",
      rowHover: "hover:bg-white/5",
    };
  };

  const themeClasses = getThemeClasses();

  if (isLoading) {
    return (
      <div
        className={`${themeClasses.container} flex flex-col gap-5 rounded-lg p-5`}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <div
              className={`h-5 w-32 animate-pulse rounded ${themeClasses.skeleton}`}
            />
            <div
              className={`h-3 w-64 animate-pulse rounded ${themeClasses.skeleton}`}
            />
          </div>
          <div className="flex flex-row gap-4">
            <div
              className={`h-8 w-24 animate-pulse rounded ${themeClasses.skeleton}`}
            />
            <div
              className={`h-8 w-20 animate-pulse rounded ${themeClasses.skeleton}`}
            />
          </div>
        </div>
        <div className="flex w-full flex-row justify-between gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div
                className={`h-3 w-24 animate-pulse rounded ${themeClasses.skeleton}`}
              />
              <div
                className={`h-5 w-32 animate-pulse rounded ${themeClasses.skeleton}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !subscriptionData) {
    return (
      <div
        className={`${themeClasses.container} flex flex-col gap-5 rounded-lg p-5`}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <div
              className={`text-lg leading-5 font-bold -tracking-wide ${themeClasses.title}`}
            >
              Subscription
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subscriptions = subscriptionData.subscriptions || [];

  return (
    <div
      className={`${themeClasses.container} flex flex-col gap-5 rounded-lg p-5`}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-2">
          <div
            className={`text-lg leading-5 font-bold -tracking-wide ${themeClasses.title}`}
          >
            Subscription
          </div>
          <div className={`text-xs leading-3 ${themeClasses.subtitle}`}>
            Manage your subscription plans
          </div>
        </div>
        <div className="flex flex-row gap-4">
          {/* <Button
            className={`gap-2 p-1.5 text-xs leading-normal font-medium tracking-normal ${themeClasses.button}`}
            variant="minimal"
            icon
          >
            <i className="ri-arrow-up-line text-xs"></i>
            Upgrade
          </Button> */}
          <Button
            className={`gap-2 p-1.5 text-xs leading-normal font-medium tracking-normal ${themeClasses.button}`}
            variant="minimal"
            icon
          >
            <i className="ri-loop-right-line text-xs"></i>
            Renew
          </Button>
        </div>
      </div>

      <div
        className="scrollbar-thin overflow-x-auto"
        style={{ scrollbarGutter: "stable" }}
      >
        <table className="w-full min-w-[800px] border-separate border-spacing-0">
          <thead>
            <tr>
              {[
                "Plan",
                "Platforms",
                "Started",
                "Until",
                "Quota",
                "Calls/Min",
                "Status",
              ].map((col) => (
                <th
                  key={col}
                  className={`px-4 py-3 text-left text-xs font-medium tracking-wide whitespace-nowrap uppercase ${themeClasses.label}`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub) => (
              <tr
                key={sub.subscription_id}
                className={`rounded-lg transition-colors ${themeClasses.rowHover}`}
              >
                <td
                  className={`px-4 py-4 text-sm font-bold ${themeClasses.value}`}
                >
                  <div className="max-w-[260px] truncate">{sub.plan_name}</div>
                </td>

                <td className="px-4 py-4">
                  <div className="flex flex-row items-center gap-2">
                    {sub.entity_types.includes("quadmedia") ? (
                      <span className="${themeClasses.value} text-sm">N/A</span>
                    ) : (
                      <>
                        {sub.platforms
                          ?.sort((a: string, b: string) => a.localeCompare(b))
                          .map((platform: string) => (
                            <img
                              key={platform}
                              src={platformIcons[platform]}
                              alt={platform}
                              className="h-5 w-5 flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ))}
                      </>
                    )}
                  </div>
                </td>

                <td
                  className={`px-4 py-4 text-sm whitespace-nowrap ${themeClasses.value}`}
                >
                  {formatSubscriptionDate(sub.start_date)}
                </td>

                <td
                  className={`px-4 py-4 text-sm whitespace-nowrap ${themeClasses.value}`}
                >
                  {formatSubscriptionDate(sub.end_date)}
                </td>

                <td className={`px-4 py-4 text-sm ${themeClasses.value}`}>
                  {sub.quota_total}
                </td>

                <td className={`px-4 py-4 text-sm ${themeClasses.value}`}>
                  {sub.calls_per_minute}
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 flex-shrink-0 rounded-full ${getStatusColor(sub.status)}`}
                    />
                    <span
                      className={`text-sm capitalize ${themeClasses.value}`}
                    >
                      {sub.status}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
